import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { DateTime } from 'luxon'

import { CommonStoreStatus } from './CommonStoreStatus'
import { SimpleIdentity, QuickProvisioningIdentity } from '../model/Identity'
import { Event, SimpleEvent, Location, Position, IdentityRole } from '../model/Event'
import { EventSchedule, ShiftSpot, Shift, ShiftRecurrence, ShiftSpotContext } from '../model/EventSchedule'
import { EventWeekHelper } from '../model/EventWeekHelper'
import { ShiftRequest } from '../requests/ShiftRequest'
import { ShiftSpotRequest } from '../requests/ShiftSpotRequest'
import { PunchMultipleAsSpecifiedRequest } from '../requests/PunchMultipleAsSpecifiedRequest'
import { LocalTimeParser, DEFAULT_TIME_FORMAT } from '../model/Common'

import EventEndpoints from '../endpoints/EventEndpoints'
import ScheduleEndpoints from '../endpoints/ScheduleEndpoints'
import { DialogManager, Application } from '../context'
import * as util from '../common/Util'

interface ScheduleState {
  // Core state
  status: CommonStoreStatus
  schedulableIdentities: Array<QuickProvisioningIdentity>
  eventSchedule: EventSchedule | null
  
  // Loading management
  waitingFor: number
  
  // Internal helpers
  _eventWeekHelper: EventWeekHelper | null
  
  // Actions
  initialize: (eventId: number) => void
  refresh: (includePunchClockState?: boolean) => Promise<EventSchedule>
  refreshShiftSpot: (shiftSpotId: number, includePunchClock?: boolean) => Promise<ShiftSpot>
  assignIdentityToSpot: (spot: ShiftSpot, identity: SimpleIdentity) => Promise<ShiftRecurrence>
  assignIdentityToMultipleSpots: (shiftSpotIds: Array<number>, identity: SimpleIdentity) => Promise<void>
  markSpotUnassignable: (spot: ShiftSpot) => Promise<ShiftRecurrence>
  markMultipleSpotsUnassignable: (shiftSpotIds: Array<number>) => Promise<void>
  punchForSpotsAsScheduled: (shiftSpotIds: Array<number>) => Promise<EventSchedule>
  punchForSpotsAsSpecified: (request: PunchMultipleAsSpecifiedRequest) => Promise<EventSchedule>
  unassignSpot: (spot: ShiftSpot) => Promise<ShiftRecurrence>
  editShiftSpotDetails: (spotId: number, request: ShiftSpotRequest) => Promise<ShiftSpot>
  createShift: (request: ShiftRequest) => Promise<EventSchedule>
  editShift: (shiftId: number, request: ShiftRequest) => Promise<EventSchedule>
  deleteShift: (shiftId: number) => Promise<EventSchedule>
  
  // Helper methods
  findSchedulableIdentitiesForRole: (role: IdentityRole) => Array<QuickProvisioningIdentity>
  findShift: (shiftId: number) => Shift | undefined
  findShiftSpotContext: (shiftSpotId: number) => ShiftSpotContext | null
  findRecurrenceContext: (recurrenceId: number) => ShiftSpotContext | null
  findShiftSpot: (shiftSpotId: number) => ShiftSpot | null
  computeHoursScheduledForWeek: (relativeWeekNum: number, identity: SimpleIdentity) => number
  
  // Loading helpers
  startLoading: () => void
  doneLoading: () => void
  
  // Computed getters
  isLoading: () => boolean
  event: () => Event | null
  shiftSpots: () => Array<ShiftSpot>
  locations: () => Array<Location>
  positions: () => Array<Position>
  schedulableIdentitiesSorted: () => Array<SimpleIdentity>
  startTimes: () => Array<Date>
  shiftsWorkedByEmployee: () => { [employeeId: number]: Array<Shift> }
  timezone: () => string
  eventWeekHelper: () => EventWeekHelper | null
  
  // Private helper methods (exposed for internal use)
  standardConsumeEndpoint: (logic: () => Promise<EventSchedule>) => Promise<EventSchedule>
  replaceShiftSpot: (shiftSpot: ShiftSpot) => void
  replaceShiftRecurrence: (recurrence: ShiftRecurrence) => void
}

const DefaultErrorHandler = (reject: any) => (err: any) => {
  console.error('Error in ScheduleStore', err)
  reject(err)
}

// Factory function to create schedule store instances
export const createScheduleStore = (eventId: number) => {
  return create<ScheduleState>()(
    devtools(
      (set, get) => ({
        // Initial state
        status: CommonStoreStatus.LOADING,
        schedulableIdentities: [],
        eventSchedule: null,
        waitingFor: 0,
        _eventWeekHelper: null,
        
        // Actions
        initialize: (id: number) => {
          // Store the event ID for use in actions
          ;(get() as any)._eventId = id
        },
        
        refresh: async (includePunchClockState: boolean = true) => {
          const { startLoading, doneLoading } = get()
          const eventId = (get() as any)._eventId
          
          startLoading()
          
          return new Promise((resolve, reject) => {
            let n = 0
            const Done = () => {
              if (++n >= 2) {
                set({ status: CommonStoreStatus.READY })
                doneLoading()
                resolve(get().eventSchedule!)
              }
            }
            
            ScheduleEndpoints
              .getEventSchedule(eventId, includePunchClockState)
              .then(eventSchedule => {
                const eventWeekHelper = new EventWeekHelper(eventSchedule)
                set({ 
                  eventSchedule, 
                  _eventWeekHelper: eventWeekHelper 
                })
                Done()
              })
              .catch(err => {
                console.error('Error in ScheduleStore', err)
                set({ status: CommonStoreStatus.ERROR })
                doneLoading()
                reject(err)
              })
            
            ScheduleEndpoints
              .getSchedulableForEvent(eventId)
              .then(identities => {
                set({ schedulableIdentities: identities })
                Done()
              })
              .catch(err => {
                console.error('Error in ScheduleStore', err)
                set({ status: CommonStoreStatus.ERROR })
                doneLoading()
                reject(err)
              })
          })
        },
        
        refreshShiftSpot: async (shiftSpotId: number, includePunchClock: boolean = true) => {
          try {
            const shiftSpot = await ScheduleEndpoints.getShiftSpot(shiftSpotId, includePunchClock)
            // Access replaceShiftSpot through the store methods
            const state = get()
            state.replaceShiftSpot(shiftSpot)
            return shiftSpot
          } catch (error) {
            throw error
          }
        },
        
        assignIdentityToSpot: async (spot: ShiftSpot, identity: SimpleIdentity) => {
          try {
            const recurrence = await ScheduleEndpoints.assignIdentityToSpot(spot.id, { 
              identityId: identity.id 
            })
            const state = get()
            state.replaceShiftRecurrence(recurrence)
            return recurrence
          } catch (error) {
            DefaultErrorHandler(() => {})(error)
            throw error
          }
        },
        
        assignIdentityToMultipleSpots: async (shiftSpotIds: Array<number>, identity: SimpleIdentity) => {
          return ScheduleEndpoints.assignIdentityToMultipleSpots({
            shiftSpotIds,
            identityId: identity.id
          })
        },
        
        markSpotUnassignable: async (spot: ShiftSpot) => {
          try {
            const recurrence = await ScheduleEndpoints.assignIdentityToSpot(spot.id, { 
              identityId: 0 
            })
            const state = get()
            state.replaceShiftRecurrence(recurrence)
            return recurrence
          } catch (error) {
            DefaultErrorHandler(() => {})(error)
            throw error
          }
        },
        
        markMultipleSpotsUnassignable: async (shiftSpotIds: Array<number>) => {
          return ScheduleEndpoints.assignIdentityToMultipleSpots({
            shiftSpotIds,
            identityId: 0
          })
        },
        
        punchForSpotsAsScheduled: async (shiftSpotIds: Array<number>) => {
          const state = get()
          return state.standardConsumeEndpoint(() =>
            ScheduleEndpoints.punchForSpotsAsScheduled({ shiftSpotIds })
          )
        },
        
        punchForSpotsAsSpecified: async (request: PunchMultipleAsSpecifiedRequest) => {
          const state = get()
          return state.standardConsumeEndpoint(() =>
            ScheduleEndpoints.punchForSpotsAsSpecified(request)
          )
        },
        
        unassignSpot: async (spot: ShiftSpot) => {
          try {
            const recurrence = await ScheduleEndpoints.unassignSpot(spot.id)
            const state = get()
            state.replaceShiftRecurrence(recurrence)
            return recurrence
          } catch (error) {
            DefaultErrorHandler(() => {})(error)
            throw error
          }
        },
        
        editShiftSpotDetails: async (spotId: number, request: ShiftSpotRequest) => {
          try {
            const shiftSpot = await ScheduleEndpoints.editShiftSpotDetails(spotId, request)
            const state = get()
            state.replaceShiftSpot(shiftSpot)
            return shiftSpot
          } catch (error) {
            DefaultErrorHandler(() => {})(error)
            throw error
          }
        },
        
        createShift: async (request: ShiftRequest) => {
          const eventId = (get() as any)._eventId
          const state = get()
          return state.standardConsumeEndpoint(() =>
            ScheduleEndpoints.createShift(eventId, request)
          )
        },
        
        editShift: async (shiftId: number, request: ShiftRequest) => {
          const state = get()
          return state.standardConsumeEndpoint(() =>
            ScheduleEndpoints.editShift(shiftId, request)
          )
        },
        
        deleteShift: async (shiftId: number) => {
          const state = get()
          return state.standardConsumeEndpoint(() =>
            ScheduleEndpoints.deleteShift(shiftId)
          )
        },
        
        // Helper methods
        findSchedulableIdentitiesForRole: (role: IdentityRole) => {
          return get().schedulableIdentities
            .filter(x => x.roleId === role.id)
            .slice()
            .sort(
              (a, b) =>
                a.firstName.localeCompare(b.firstName) ||
                a.lastName.localeCompare(b.lastName)
            )
        },
        
        findShift: (shiftId: number) => {
          const { eventSchedule } = get()
          if (!eventSchedule) return undefined
          return eventSchedule.shifts.find(s => s.id === shiftId)
        },
        
        findShiftSpotContext: (shiftSpotId: number) => {
          const { eventSchedule } = get()
          if (!eventSchedule) return null
          
          for (let i = 0; i < eventSchedule.shifts.length; i++) {
            const shift = eventSchedule.shifts[i]
            for (let j = 0; j < shift.recurrences.length; j++) {
              const recurrence = shift.recurrences[j]
              for (let k = 0; k < recurrence.spots.length; k++) {
                const shiftSpot = recurrence.spots[k]
                if (shiftSpot.id === shiftSpotId) {
                  return { shift, recurrence, shiftSpot }
                }
              }
            }
          }
          return null
        },
        
        findRecurrenceContext: (recurrenceId: number) => {
          const { eventSchedule } = get()
          if (!eventSchedule) return null
          
          for (let i = 0; i < eventSchedule.shifts.length; i++) {
            const shift = eventSchedule.shifts[i]
            for (let j = 0; j < shift.recurrences.length; j++) {
              const recurrence = shift.recurrences[j]
              if (recurrence.id === recurrenceId) {
                return { shift, recurrence, shiftSpot: null }
              }
            }
          }
          return null
        },
        
        findShiftSpot: (shiftSpotId: number) => {
          const state = get()
          return state.shiftSpots().find(s => s.id === shiftSpotId) || null
        },
        
        computeHoursScheduledForWeek: (relativeWeekNum: number, identity: SimpleIdentity) => {
          const { _eventWeekHelper } = get()
          if (!_eventWeekHelper) return 0
          
          const week = _eventWeekHelper.getWeekByRelativeWeekNum(relativeWeekNum)
          const state = get()
          const employeeShifts = state.shiftsWorkedByEmployee()[identity.id] || []
          
          return employeeShifts
            .map(shift =>
              shift.recurrences
                .filter(r => {
                  const recurrenceDate = new Date(r.date)
                  return DateTime.fromJSDate(week.firstDay) <= DateTime.fromJSDate(recurrenceDate) && 
                    DateTime.fromJSDate(week.lastDay) >= DateTime.fromJSDate(recurrenceDate) &&
                    r.spots.findIndex(s => s.fulfilledBy && s.fulfilledBy.id === identity.id) >= 0
                })
                .map(a => {
                  const beginTime = DateTime.fromFormat(shift.beginTime, DEFAULT_TIME_FORMAT.replace('HH', 'HH').replace('mm', 'mm'))
                  const endTime = DateTime.fromFormat(shift.endTime, DEFAULT_TIME_FORMAT.replace('HH', 'HH').replace('mm', 'mm'))
                  if (beginTime < endTime) {
                    return endTime.diff(beginTime, 'hours').hours
                  } else {
                    return (24 - beginTime.diff(endTime, 'hours').hours)
                  }
                })
                .reduce((a, b) => a + b, 0)
            )
            .reduce((a, b) => a + b, 0)
        },
        
        // Loading helpers
        startLoading: () => set((state) => ({ 
          waitingFor: state.waitingFor + 1 
        })),
        
        doneLoading: () => set((state) => ({ 
          waitingFor: Math.max(0, state.waitingFor - 1) 
        })),
        
        // Computed getters
        isLoading: () => get().waitingFor > 0,
        
        event: () => get().eventSchedule,
        
        shiftSpots: () => {
          const { eventSchedule } = get()
          if (!eventSchedule) return []
          
          const spots: Array<ShiftSpot> = []
          eventSchedule.shifts.forEach(shift =>
            shift.recurrences.forEach(r =>
              r.spots.forEach(spot =>
                spots.push(spot)
              )
            )
          )
          return spots
        },
        
        locations: () => {
          const { eventSchedule } = get()
          if (!eventSchedule) return []
          
          return eventSchedule.locations
            .slice()
            .sort((a, b) => a.name > b.name ? 1 : -1)
        },
        
        positions: () => {
          const { eventSchedule } = get()
          if (!eventSchedule) return []
          
          return eventSchedule.positions
            .slice()
            .sort((a, b) => a.name > b.name ? 1 : -1)
        },
        
        schedulableIdentitiesSorted: () => {
          return get().schedulableIdentities
            .slice()
            .sort(
              (a, b) =>
                a.firstName.localeCompare(b.firstName) ||
                a.lastName.localeCompare(b.lastName)
            )
        },
        
        startTimes: () => {
          const { eventSchedule } = get()
          if (!eventSchedule) return []
          
          return eventSchedule.shifts
            .map(e => LocalTimeParser(e.beginTime))
            .reduce((list, b) => 
              list.findIndex(c => c.getTime() === b.getTime()) >= 0 ? list : [...list, b], 
              [] as Array<Date>
            )
            .sort((a, b) => DateTime.fromJSDate(a) > DateTime.fromJSDate(b) ? 1 : -1)
        },
        
        shiftsWorkedByEmployee: () => {
          const { schedulableIdentities, eventSchedule } = get()
          if (!eventSchedule) return {}
          
          const value: { [employeeId: number]: Array<Shift> } = {}
          schedulableIdentities.forEach(id => {
            value[id.id] = eventSchedule.shifts
              .filter(shift =>
                shift.recurrences.findIndex(r => 
                  r.spots.findIndex(spot => 
                    spot.fulfilledBy && spot.fulfilledBy.id === id.id
                  ) >= 0
                ) >= 0
              )
          })
          return value
        },
        
        timezone: () => {
          const { eventSchedule } = get()
          if (!eventSchedule) return ''
          return eventSchedule.timezone || eventSchedule.client.timezone
        },
        
        eventWeekHelper: () => get()._eventWeekHelper,
        
        // Private helper methods
        standardConsumeEndpoint: (logic: () => Promise<EventSchedule>) => {
          return new Promise<EventSchedule>((resolve, reject) => {
            logic()
              .then(eventSchedule => {
                set({ eventSchedule })
                resolve(eventSchedule)
              })
              .catch(DefaultErrorHandler(reject))
          })
        },
        
        replaceShiftSpot: (shiftSpot: ShiftSpot) => {
          const ctx = get().findShiftSpotContext(shiftSpot.id)
          if (!ctx) {
            DialogManager.showAlert('Assigned shift does not exist in store. This is a bug, please contact developer.')
            Application.startLoading()
            return
          }
          
          const i = ctx.recurrence.spots.findIndex(s => s.id === shiftSpot.id)
          ctx.recurrence.spots[i] = shiftSpot
          set({ 
            eventSchedule: util.DeepCopy(get().eventSchedule) 
          })
        },
        
        replaceShiftRecurrence: (recurrence: ShiftRecurrence) => {
          const ctx = get().findRecurrenceContext(recurrence.id)
          if (!ctx) {
            DialogManager.showAlert('Recurrence does not exist in store. This is a bug, please contact developer.')
            Application.startLoading()
            return
          }
          
          const i = ctx.shift.recurrences.findIndex(r => r.id === recurrence.id)
          ctx.shift.recurrences[i] = recurrence
          set({ 
            eventSchedule: util.DeepCopy(get().eventSchedule) 
          })
        }
      }),
      {
        name: `schedule-store-${eventId}`,
      }
    )
  )
}

// Export a function to create schedule stores
export const useScheduleStore = (eventId: number) => {
  // Create a new store instance for each event ID
  const store = createScheduleStore(eventId)
  
  // Initialize the store with the event ID
  store.getState().initialize(eventId)
  
  return store
}